import { useParams, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { renderMarkdown } from "../lib/markdown";
import { createPage } from "../lib/api";
import { useSpaceContext } from "./SpaceLayout";

export default function NewPagePage() {
  const { username, spaceSlug } = useParams<{
    username: string;
    spaceSlug: string;
  }>();
  const navigate = useNavigate();
  const { refreshPages, isOwner } = useSpaceContext();

  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [commitMessage, setCommitMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const renderedHtml = content ? renderMarkdown(content) : "";

  const handleCreate = async (publish: boolean) => {
    if (!username || !spaceSlug) return;
    if (!slug.trim() || !title.trim()) {
      setError("Slug and title are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createPage(username, spaceSlug, {
        slug: slug.trim(),
        title: title.trim(),
        content,
        commit_message: commitMessage || undefined,
        publish: publish && isOwner ? true : undefined,
      });
      refreshPages();
      if (publish && isOwner) {
        navigate(`/${username}/${spaceSlug}/${slug.trim()}`);
      } else {
        navigate(`/${username}/${spaceSlug}/${slug.trim()}/edit`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create page");
      setSaving(false);
    }
  };

  return (
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          New Page
        </h1>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* Slug and Title */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Slug <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="my-page or section/my-page"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="My Page Title"
            />
          </div>
        </div>

        {/* Commit message */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Commit message (optional)
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={commitMessage}
            onChange={(e) => setCommitMessage(e.target.value)}
            placeholder="Initial page creation"
          />
        </div>

        {/* Editor / Preview 2-column layout */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Editor column */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Markdown
            </label>
            <textarea
              className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-3 text-sm font-mono bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ minHeight: "400px" }}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your content in Markdown..."
            />
          </div>

          {/* Preview column */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Preview
            </label>
            <div
              className="prose dark:prose-invert max-w-none border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-gray-50 dark:bg-gray-900 overflow-auto"
              style={{ minHeight: "400px" }}
              dangerouslySetInnerHTML={{ __html: renderedHtml }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {isOwner && (
            <button
              onClick={() => handleCreate(true)}
              disabled={saving}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {saving ? "Publishing..." : "Publish"}
            </button>
          )}
          <button
            onClick={() => handleCreate(false)}
            disabled={saving}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {saving ? "Saving..." : isOwner ? "Save as Draft" : "Create Page"}
          </button>
          <Link
            to={`/${username}/${spaceSlug}`}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors"
          >
            Cancel
          </Link>
        </div>
      </div>
    </main>
  );
}
